import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { graphqlRequest } from './graphql'
import type {
  EggPricesResponse,
  PricingSettingsResponse,
  UpdatePricingInput as SharedUpdatePricingInput,
  CommissionSettingResponse,
  CreateCommissionInput,
  DriverEarningsResponse,
} from '@shinkansen/graphql'

// Re-export types from shared package for convenience
export type { EggPricesResponse, PricingSettingsResponse, CommissionSettingResponse, DriverEarningsResponse }

// NOTE FOR FUTURE AGENTS: Always use types from @shinkansen/graphql package.
// Run `pnpm graphql:export && pnpm graphql:generate` after adding new GraphQL types to Hikari.

/**
 * Egg prices interface - alias for shared type
 */
export type EggPrices = EggPricesResponse

/**
 * Pricing settings interface - alias for shared type
 */
export type PricingSettings = PricingSettingsResponse

/**
 * Input for updating pricing - using shared type from GraphQL schema
 */
export type UpdatePricingInput = SharedUpdatePricingInput

/**
 * Hook to fetch pricing settings
 */
export function usePricingSettings() {
  return useQuery({
    queryKey: ['pricingSettings'],
    queryFn: async () => {
      const result = await graphqlRequest<{ pricingSettings: PricingSettings }>(`
        query PricingSettings {
          pricingSettings {
            eggPrices {
              small
              medium
              large
            }
            packSize
            packDiscountPercent
          }
        }
      `)
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.pricingSettings
    },
  })
}

/**
 * Hook to update pricing settings (admin only)
 */
export function useUpdatePricing() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: UpdatePricingInput) => {
      const result = await graphqlRequest<{ updatePricingSettings: PricingSettings }>(`
        mutation UpdatePricing($input: UpdatePricingInput!) {
          updatePricingSettings(input: $input) {
            eggPrices {
              small
              medium
              large
            }
            packSize
            packDiscountPercent
          }
        }
      `, { input })
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.updatePricingSettings
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricingSettings'] })
    },
  })
}

/**
 * Hook to fetch current commission rate (admin only)
 */
export function useCurrentCommission() {
  return useQuery({
    queryKey: ['currentCommission'],
    queryFn: async () => {
      const result = await graphqlRequest<{ currentCommission: CommissionSettingResponse | null }>(`
        query CurrentCommission {
          currentCommission {
            id
            amountPerOrder
            effectiveFrom
            createdAt
          }
        }
      `)
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.currentCommission
    },
  })
}

/**
 * Hook to fetch commission history (admin only)
 */
export function useCommissionHistory() {
  return useQuery({
    queryKey: ['commissionHistory'],
    queryFn: async () => {
      const result = await graphqlRequest<{ commissionHistory: CommissionSettingResponse[] }>(`
        query CommissionHistory {
          commissionHistory {
            id
            amountPerOrder
            effectiveFrom
            createdAt
          }
        }
      `)
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.commissionHistory ?? []
    },
  })
}

/**
 * Hook to create new commission rate (admin only)
 */
export function useCreateCommission() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateCommissionInput) => {
      const result = await graphqlRequest<{ createCommission: CommissionSettingResponse }>(`
        mutation CreateCommission($input: CreateCommissionInput!) {
          createCommission(input: $input) {
            id
            amountPerOrder
            effectiveFrom
            createdAt
          }
        }
      `, { input })
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.createCommission
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentCommission'] })
      queryClient.invalidateQueries({ queryKey: ['commissionHistory'] })
    },
  })
}

/**
 * Hook to fetch driver earnings for a period (admin only)
 */
export function useDriverEarnings(periodStart: string, periodEnd: string) {
  return useQuery({
    queryKey: ['driverEarnings', periodStart, periodEnd],
    queryFn: async () => {
      const result = await graphqlRequest<{ driverEarnings: DriverEarningsResponse[] }>(`
        query DriverEarnings($period: EarningsPeriodInput!) {
          driverEarnings(period: $period) {
            driverId
            driverName
            totalDeliveries
            totalCommission
            periodStart
            periodEnd
          }
        }
      `, { period: { start: periodStart, end: periodEnd } })
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.driverEarnings ?? []
    },
    enabled: !!periodStart && !!periodEnd,
  })
}
